import { useNavigate } from 'react-router-dom';
import {
  FolderKanban,
  FileText,
  AlertTriangle,
  MessageCircle,
  TrendingUp,
  Clock,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { formatDate, formatCurrency, isOverdue, isApproaching, isAdminRole } from '../lib/utils';
import { ProjectBadge, InvoiceBadge, StageBadge } from '../components/Badge';
import ProgressBar from '../components/ProgressBar';

export default function Dashboard() {
  const currentUser = useStore((s) => s.currentUser);
  const projects = useStore((s) => s.projects);
  const clients = useStore((s) => s.clients);
  const invoices = useStore((s) => s.invoices);
  const messages = useStore((s) => s.messages);
  const stages = useStore((s) => s.stages);
  const users = useStore((s) => s.users);
  const navigate = useNavigate();

  // Filter based on role
  const visibleProjects = projects.filter((p) => {
    if (isAdminRole(currentUser?.role)) return true;
    if (currentUser?.role === 'sales') return p.salesOwnerId === currentUser.id;
    if (currentUser?.role === 'pm') return p.projectManagerId === currentUser.id;
    if (currentUser?.role === 'sales_pm') return p.salesOwnerId === currentUser.id || p.projectManagerId === currentUser.id;
    return false;
  });

  const activeProjects = visibleProjects.filter((p) => p.status === 'active');

  const visibleInvoices = invoices.filter((inv) =>
    visibleProjects.some((p) => p.id === inv.projectId)
  );

  const upcomingInvoices = visibleInvoices.filter(
    (inv) => (inv.status === 'not_ready' || inv.status === 'ready_to_send') && isApproaching(inv.plannedSendDate, 14)
  );

  const overdueInvoices = visibleInvoices.filter(
    (inv) => inv.status === 'overdue' || (inv.status !== 'paid' && inv.status !== 'sent' && isOverdue(inv.plannedSendDate))
  );

  const unresolvedQuestions = messages.filter(
    (m) => m.isQuestion && !m.resolved && visibleProjects.some((p) => p.id === m.projectId)
  );

  const atRiskProjects = activeProjects.filter((p) => {
    const projectStages = stages.filter((s) => s.projectId === p.id);
    return projectStages.some((s) => s.status === 'blocked') || isApproaching(p.endDate, 30);
  });

  const getUser = (id: string) => users.find((u) => u.id === id);
  const getClient = (id: string) => clients.find((c) => c.id === id);
  const getProject = (id: string) => projects.find((p) => p.id === id);

  const STAT_CARDS = [
    {
      label: 'Active Projects',
      value: activeProjects.length,
      icon: FolderKanban,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      onClick: () => navigate('/projects'),
    },
    {
      label: 'Upcoming Invoices',
      value: upcomingInvoices.length,
      icon: FileText,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
    },
    {
      label: 'Overdue Invoices',
      value: overdueInvoices.length,
      icon: AlertTriangle,
      color: 'text-red-600',
      bg: 'bg-red-50',
    },
    {
      label: 'Unresolved Questions',
      value: unresolvedQuestions.length,
      icon: MessageCircle,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'},{' '}
          {currentUser?.name.split(' ')[0]}
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          Here's what needs your attention today, {formatDate(new Date().toISOString())}.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {STAT_CARDS.map(({ label, value, icon: Icon, color, bg, onClick }) => (
          <button
            key={label}
            onClick={onClick}
            className="bg-white rounded-xl border border-gray-100 p-5 text-left hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500">{label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
              </div>
              <div className={`${bg} p-2.5 rounded-lg`}>
                <Icon size={18} className={color} />
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Projects */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100">
          <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 text-sm">Active Projects</h2>
            <button
              onClick={() => navigate('/projects')}
              className="text-xs text-brand-500 hover:text-brand-700 font-medium flex items-center gap-1"
            >
              View all <ChevronRight size={13} />
            </button>
          </div>
          <div className="divide-y divide-gray-50">
            {activeProjects.length === 0 && (
              <div className="px-5 py-8 text-center text-gray-400 text-sm">No active projects</div>
            )}
            {activeProjects.map((project) => {
              const client = getClient(project.clientId);
              const pm = getUser(project.projectManagerId);
              const hasBlocker = stages.filter((s) => s.projectId === project.id).some((s) => s.status === 'blocked');
              return (
                <button
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-gray-900 text-sm truncate">{project.name}</span>
                        {hasBlocker && (
                          <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded-md font-medium">
                            <AlertCircle size={11} /> Blocked
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{client?.companyName} · PM: {pm?.name}</p>
                      <div className="mt-2.5">
                        <ProgressBar value={project.overallCompletion} showLabel />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(project.value, project.currency)}
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">Due {formatDate(project.endDate)}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* At Risk */}
          {atRiskProjects.length > 0 && (
            <div className="bg-red-50 rounded-xl border border-red-100">
              <div className="px-5 py-4 border-b border-red-100 flex items-center gap-2">
                <AlertTriangle size={15} className="text-red-500" />
                <h2 className="font-semibold text-red-700 text-sm">Projects at Risk</h2>
              </div>
              <div className="p-3 space-y-2">
                {atRiskProjects.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/projects/${p.id}`)}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-white rounded-lg hover:bg-red-50/50 text-left transition-colors border border-red-100"
                  >
                    <span className="text-sm text-gray-800 font-medium truncate">{p.name}</span>
                    <ChevronRight size={14} className="text-gray-400 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Overdue Invoices */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
              <Clock size={15} className="text-orange-500" />
              <h2 className="font-semibold text-gray-900 text-sm">Overdue Invoices</h2>
            </div>
            <div className="p-3 space-y-2">
              {overdueInvoices.length === 0 && (
                <div className="px-2 py-4 text-center">
                  <CheckCircle2 size={20} className="text-green-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">All invoices up to date</p>
                </div>
              )}
              {overdueInvoices.slice(0, 4).map((inv) => {
                const project = getProject(inv.projectId);
                return (
                  <button
                    key={inv.id}
                    onClick={() => navigate(`/projects/${inv.projectId}/invoices`)}
                    className="w-full px-3 py-2.5 bg-orange-50 border border-orange-100 rounded-lg hover:bg-orange-100/50 text-left transition-colors"
                  >
                    <div className="text-xs font-medium text-gray-800 truncate">{inv.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex items-center justify-between">
                      <span>{project?.name}</span>
                      <span className="font-medium text-orange-600">{formatCurrency(inv.amount)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Unresolved Q&A */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
              <MessageCircle size={15} className="text-purple-500" />
              <h2 className="font-semibold text-gray-900 text-sm">Open Questions</h2>
            </div>
            <div className="p-3 space-y-2">
              {unresolvedQuestions.length === 0 && (
                <div className="px-2 py-4 text-center">
                  <CheckCircle2 size={20} className="text-green-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-400">No open questions</p>
                </div>
              )}
              {unresolvedQuestions.slice(0, 3).map((msg) => {
                const project = getProject(msg.projectId);
                const sender = getUser(msg.senderId);
                return (
                  <button
                    key={msg.id}
                    onClick={() => navigate(`/projects/${msg.projectId}/chat`)}
                    className="w-full px-3 py-2.5 bg-purple-50 border border-purple-100 rounded-lg hover:bg-purple-100/50 text-left transition-colors"
                  >
                    <div className="text-xs text-gray-800 line-clamp-2">{msg.message}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {sender?.name} · {project?.name}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
